import { useDispatch, useSelector } from "react-redux";

import { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";

import { ALL_COLORS, COLORS } from "@/constants/colors";
import {
  DEFAULT_STEP,
  PROFILING_LAYER_TYPES,
  ROW_ID_DETAIL_DATASET,
  SPATIAL_ANALYSIS_RESULT_DATASET_NAME,
  SPATIAL_TYPE_TO_LAYER_TYPE
} from "@/constants/dataset";
import {
  AREA_INPUT_SOURCE_IDS,
  CATCHMENT_DATASET_POLYGON_SOURCE_ID,
  CATCHMENT_RADIUS_RESULT_SOURCE_ID,
  SUBLAYERS
} from "@/constants/map";
import { RootState, store } from "@/store";
import {
  addDataset,
  addFilter,
  addRawDataset,
  cancelDatasetCustomCategoryConfig,
  cancelDatasetCustomRangeConfig,
  cancelDatasetSingleStyleConfig,
  cleanupVisualizationOrder,
  clearDataset,
  commitDatasetCustomCategoryConfig,
  commitDatasetCustomRangeConfig,
  commitDatasetSingleStyleConfig,
  incrementDatasetCount,
  initializeWeightValues,
  previewDatasetCustomCategoryConfig,
  previewDatasetCustomRangeConfig,
  previewDatasetSingleStyleConfig,
  removeDataset,
  removeRawDataset,
  reorderDatasets,
  resetDatasetToHistoryDataset,
  seedDatasetCustomCategoryConfig,
  setDatasetIsFetching,
  setWeightValuesForDatasetId,
  syncDatasetCustomCategoryConfig,
  toggleDatasetVisibility,
  updateDatasetColorScaleMethod,
  updateDatasetColorScaleSteps,
  updateDatasetColorStyle,
  updateDatasetGroupBy,
  updateDatasetH3Config,
  updateDatasetOpacity,
  updateDatasetSelectedCategory,
  updateDatasetStrokeAttribute,
  updateDatasetStrokeOpacity,
  updateDatasetStrokeWidth,
  updateDatasetStyleAttributeLabel,
  updateDatasetStyleColorLabel,
  updateDatasetStyleEnabledRank,
  updateDatasetStyleMaxRanking,
  updateDatasetStyleOpacityLabel,
  updateDatasetStyleVisibleLabel
} from "@/store/slice/datasetSlice";
import { setLegendFilters, setSelectedFeature } from "@/store/slice/mapSlice";
import {
  clearInputRowCounts,
  removeInputRowCount,
  setDatasetConfigTemp,
  setInputRowCount,
  setSpatialConfig
} from "@/store/slice/spatialSlice";
import {
  ESidebarMenuType,
  setLoadingOverlay,
  setSideBarMenuTypePopupDisplay
} from "@/store/slice/uiSlice";
import {
  addWidget,
  removeWidgetFromDataset,
  resetWidgetToHistory,
  updateWidgetGroupBy,
  updateWidgetH3Config
} from "@/store/slice/widgetSlice";
import { SpatialAggregationType } from "@/types/common";
import {
  ColorScaleMethod,
  CustomCategoryValue,
  CustomRangeValue,
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetDetailResponse,
  DatasetFilter,
  DatasetHistory,
  DatasetIndices,
  DatasetMetadata,
  DatasetStyle,
  EColorLabel,
  EColorNames,
  H3Configuration,
  SingleStyleValue,
  StyleConfig,
  WeightValueEntry,
  WeightValues
} from "@/types/dataset";
import { LayerType } from "@/types/map";
import { EOutputAnalysisType, ScoringOptionsEnum } from "@/types/spatial";
import { WidgetConfigWithoutWidgetId } from "@/types/widget";
import { generateDatasetId } from "@/utils/common";
import {
  convertDatasetResponseToFeatureCollection,
  createDatasetAttributes,
  createDefaultSpatialDataset,
  createNewDataset,
  getAttributeTableValues,
  getColorRangesMinMax,
  getCustomCategoryActiveStepCount,
  getDatasetVisibility,
  getDefaultCustomCategoryConfig,
  getDefaultCustomRangeConfig,
  getDefaultSelectedCategory,
  getDefaultSingleStyleConfig,
  getNumericRange,
  hasCustomCategoryAssignments,
  hasEmptyAttributeTableValues,
  hasNullColorRange,
  isIntegerDataType,
  recolorCustomCategoryConfig,
  recolorCustomRangeConfig,
  reconcileCustomCategoryConfig,
  redistributeCustomRange
} from "@/utils/dataset";
import { generateDatasetData } from "@/utils/generateDatasetData";
import {
  changeColorCustomCategoryLayer,
  changeColorCustomRangeLayer,
  changeColorSingleStyleLayer,
  changeStrokeWidthLayer,
  getBeforeId,
  getSourceProperties,
  hasEmptyAttributeValuesOnMap,
  resetSingleStyleMarker,
  toggleLayerVisibilityById
} from "@/utils/map";
import { computeStepValue } from "@/utils/stepValue";
import { wktToGeoJSON } from "@/utils/wkt";

import { useAppFeatures } from "../useAppFeatures";

import { useMap } from "./core/useMapContext";

/**
 * The attribute a layer is styled by: the stroke attribute for line datasets
 * (which have no fill layer), the selected fill category otherwise.
 */
const getStyledCategory = (dataset: Dataset): DatasetAttributeSchema =>
  dataset.properties.spatialType === LayerType.LINE
    ? (dataset.categories.find(
        (category) =>
          category.attribute_name === dataset.style.stroke?.attribute_name
      ) ?? dataset.selectedCategory)
    : dataset.selectedCategory;

type HandleAddDatasetType = {
  datasetMetadata: DatasetDetailResponse | DatasetMetadata;
  datasetData: DatasetData;
  savedFilters: DatasetFilter[];
  attributesSchema: DatasetAttributeSchema[];
  datasetIndex?: number;
  h3Configuration?: H3Configuration | null;
  weightValues?: WeightValues[string];
  withoutAttributeSchema?: boolean;
  styleConfig?: StyleConfig;
  index?: number;
  group_by?: string;
  is_global_admin_area?: boolean;
  widget?: WidgetConfigWithoutWidgetId[];
};

type HandleAddOnboardingDatasetType = {
  datasetDetail: DatasetDetailResponse | DatasetMetadata;
  datasetData: DatasetData;
  savedFilters: DatasetFilter[];
  attributesSchema: DatasetAttributeSchema[];
};

export const useDataset = () => {
  const dispatch = useDispatch();
  const {
    map,
    handleAddLineLayer,
    handleRemoveSourceLayer,
    handleAddPolygonLayer,
    handleChangeColorDataLayers,
    handleChangeColorLayers,
    handleChangeOpacityLayers,
    handleAddPointsLayer,
    handleAddGridLayer,
    handleAddSiteProfiling,
    handleResetLayerOrder,
    handleResetLayerData,
    removeAllCatchmentMarkers,
    showAllMarkers,
    hideAllMarkers,
    handleAddH3GeohashLayer,
    handleChangeVisibleLabelLayer,
    handleDisplayNameLabelLayerCategory,
    handleChangeLabelLayerColor,
    handleChangeLabelLayerOpacity,
    handleStyleRankEnabledChange,
    handleUpdateSourceWithRank,
    handleHighlightFeatureByGeometry,
    handleMovePositionToLayer,
    handleFilterLayerByLegend
  } = useMap();

  const { datasets, rawDatasets, visualizationOrder, datasetIndices, history } =
    useSelector((state: RootState) => state.dataset);
  const { selectedFeature, legendFilters } = useSelector(
    (state: RootState) => state.map
  );
  const { isAdminTreeEnabled, isCustomMapVisualizationLegendEnabled } =
    useAppFeatures();
  const { showCatchmentPopup } = useSelector((state: RootState) => state.ui);
  const { catchmentSettingsTemp, spatialConfig } = useSelector(
    (state: RootState) => state.spatial
  );

  // Func to add dataset, and show the dataset in the map
  const handleAddDatasetAndFeatureData = (
    datasetIdOriginal: string,
    newDataset: Dataset,
    datasetIndex: number,
    savedFilter: DatasetFilter[],
    featureCollection: FeatureCollection<Geometry, GeoJsonProperties>,
    isCalculateWeight?: boolean
  ) => {
    dispatch(
      incrementDatasetCount({
        baseId: datasetIdOriginal,
        usedIndex: datasetIndex
      })
    );
    dispatch(addDataset({ dataset: newDataset, isCalculateWeight }));
    dispatch(cleanupVisualizationOrder());
    dispatch(addFilter({ datasetId: newDataset.data.id, filter: savedFilter }));

    const spatialType = newDataset.properties.spatialType;
    if (
      spatialType === LayerType.POLYGON ||
      spatialType === LayerType.ADMINISTRATIVE
    ) {
      handleAddPolygonLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.POINT) {
      handleAddPointsLayer(newDataset, featureCollection);
    } else if (
      spatialType === LayerType.H3 ||
      spatialType === LayerType.GEOHASH
    ) {
      handleAddH3GeohashLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.LINE) {
      handleAddLineLayer(newDataset, featureCollection);
    }
  };

  /**
   * Stages a dataset as a catchment input (instead of adding it to the analysis
   * canvas) by writing it into `catchmentSettingsTemp.datasetConfig`. Shared by
   * the Data Explore single-add path (`DatasetRightAttrFilter`) and the POI
   * confirmation batch path (`usePoiConfirmation`).
   *
   * @param batchIndex - 0-based position within a confirmation batch, used only
   *   to vary the default color. Omit for the single-add path to keep the
   *   default color (`createNewDataset` falls back to `ALL_COLORS[0]`).
   */
  const handleAddDatasetShowCatchment = ({
    dataset,
    rawDataset,
    attributesSchema,
    filters,
    batchIndex,
    is_global_admin_area = false
  }: {
    dataset: DatasetDetailResponse | DatasetMetadata;
    rawDataset: DatasetData;
    attributesSchema: DatasetAttributeSchema[];
    filters: DatasetFilter[];
    batchIndex?: number;
    is_global_admin_area?: boolean;
  }) => {
    const datasetConfig = catchmentSettingsTemp?.datasetConfig;
    if (!datasetConfig) return;

    const { id, nextIndex } = generateDatasetId(
      dataset.id,
      datasetConfig.datasetIndices
    );
    const datasetName =
      nextIndex > 1 ? `${dataset.name} (${nextIndex - 1})` : dataset.name;

    // Color index must stay within the palette: `createNewDataset` indexes
    // `ALL_COLORS` directly (no modulo, unlike `handleAddDataset`), so an
    // out-of-range value yields an undefined color family and crashes. For the
    // batch path, base it on the catchment list and wrap by palette size; for
    // the single-add path (no batchIndex) leave it undefined so the default
    // color applies.
    const colorIndex =
      batchIndex === undefined
        ? undefined
        : (Object.keys(datasetConfig.datasets).length + batchIndex) %
          ALL_COLORS.length;

    const newDataset = createNewDataset({
      dataset,
      datasetId: id,
      datasetName,
      attributesSchema,
      index: colorIndex,
      is_global_admin_area,
      showCatchmentPopup
    });
    const featureCollection =
      convertDatasetResponseToFeatureCollection(rawDataset);
    handleMovePositionToLayer(featureCollection);
    dispatch(
      setDatasetConfigTemp({
        uniqueId: id,
        originalId: dataset.id,
        dataset: newDataset,
        rawDataset,
        filters,
        nextIndex
      })
    );
  };

  const handleAddDataset = async ({
    datasetMetadata,
    datasetData,
    savedFilters,
    attributesSchema,
    datasetIndex,
    h3Configuration,
    weightValues,
    withoutAttributeSchema,
    styleConfig,
    index,
    group_by,
    is_global_admin_area = false,
    widget
  }: HandleAddDatasetType) => {
    const { id, nextIndex } = datasetIndex
      ? {
          id: datasetMetadata.id + "_" + datasetIndex,
          nextIndex: datasetIndex
        }
      : generateDatasetId(datasetMetadata.id, datasetIndices);

    const featureCollection = convertDatasetResponseToFeatureCollection(
      datasetData,
      withoutAttributeSchema ? undefined : attributesSchema
    );

    const datasetName =
      nextIndex > 1
        ? `${datasetMetadata.name} (${nextIndex - 1})`
        : datasetMetadata.name;

    const resolvedDisplayName = datasetMetadata.display_name
      ? datasetMetadata.display_name
      : datasetName;

    const newDataset = createNewDataset({
      dataset: datasetMetadata,
      datasetId: id,
      datasetName: resolvedDisplayName,
      attributesSchema,
      h3Configuration,
      styleConfig,
      totalRows: datasetData.rows.length,
      index: (index ?? 0) % 20,
      group_by,
      is_global_admin_area
    });

    handleAddDatasetAndFeatureData(
      datasetMetadata.id,
      newDataset,
      nextIndex,
      savedFilters,
      featureCollection,
      !weightValues
    );

    if (widget?.length) {
      // addWidget prepends to the widget order, so iterate in reverse to keep
      // the saved top-to-bottom ordering once every widget is re-added.
      [...widget].reverse().forEach((w) => {
        dispatch(
          addWidget({
            id: id,
            widget: {
              ...w,
              is_default: w.is_default ?? false,
              label: w.label ? w.label : resolvedDisplayName,
              dataset_id: id
            }
          })
        );
      });
    }
    dispatch(addRawDataset({ dataset: datasetData, id }));
    if (isAdminTreeEnabled) {
      dispatch(
        setInputRowCount({ datasetId: id, count: datasetData.rows.length })
      );
    }

    // Check if exist weight values for the dataset will be added, otherwise initialize weight values
    if (weightValues && Object.keys(weightValues).length > 0) {
      dispatch(
        setWeightValuesForDatasetId({
          datasetId: newDataset.data.id,
          data: weightValues
        })
      );
    } else dispatch(initializeWeightValues());

    return newDataset;
  };

  const handleAddOnboardingDataset = async ({
    datasetDetail,
    datasetData,
    savedFilters,
    attributesSchema
  }: HandleAddOnboardingDatasetType) => {
    // Onboarding fixtures already contain final rows, so mirror the stream
    // completion path without starting SSE/network work.
    const newDataset = await handleAddDataset({
      datasetMetadata: datasetDetail,
      datasetData,
      savedFilters,
      attributesSchema,
      h3Configuration: null,
      index: Object.keys(datasets).length
    });

    dispatch(setLoadingOverlay(false));
    dispatch(setSideBarMenuTypePopupDisplay(ESidebarMenuType.SPATIAL));

    return newDataset;
  };

  type HandleCreateDatasetHistoryType = {
    datasetMetadata: DatasetDetailResponse;
    datasetData: DatasetData;
    savedFilters: DatasetFilter[];
    attributesSchema: DatasetAttributeSchema[];
    datasetIndex?: number;
    h3Configuration?: H3Configuration | null;
    weightValues?: WeightValues[string];
    styleConfig?: StyleConfig;
    datasetHistory: DatasetHistory;
    datasetIndices: DatasetIndices;
    group_by?: string;
    is_global_admin_area?: boolean;
  };
  const handleCreateDatasetHistory = async ({
    datasetMetadata,
    datasetData,
    savedFilters,
    attributesSchema,
    h3Configuration,
    weightValues,
    styleConfig,
    datasetHistory,
    datasetIndices,
    group_by,
    is_global_admin_area = false
  }: HandleCreateDatasetHistoryType) => {
    const { id, nextIndex } = generateDatasetId(
      datasetMetadata.id,
      datasetIndices
    );

    dispatch(
      incrementDatasetCount({
        baseId: datasetMetadata.id,
        usedIndex: nextIndex
      })
    );

    if (!datasetIndices[datasetMetadata.id]) {
      datasetIndices[datasetMetadata.id] = { counter: 0, usedIndices: [] };
    }
    datasetIndices[datasetMetadata.id].counter++;
    datasetIndices[datasetMetadata.id].usedIndices.push(nextIndex);

    const datasetName =
      nextIndex > 1
        ? `${datasetMetadata.name} (${nextIndex - 1})`
        : datasetMetadata.name;

    const resolvedDisplayName = datasetMetadata.display_name
      ? datasetMetadata.display_name
      : datasetName;

    const newDataset = createNewDataset({
      dataset: datasetMetadata,
      datasetId: id,
      datasetName: resolvedDisplayName,
      attributesSchema,
      h3Configuration,
      styleConfig,
      group_by,
      is_global_admin_area,
      totalRows: datasetData.rows.length
    });

    datasetHistory.datasets[newDataset.data.id] = newDataset;
    datasetHistory.rawDatasets[newDataset.data.id] = datasetData;
    datasetHistory.visualizationOrder.unshift(newDataset.data.id);

    dispatch(
      addFilter({ datasetId: newDataset.data.id, filter: savedFilters })
    );
    if (weightValues && Object.keys(weightValues).length > 0) {
      dispatch(
        setWeightValuesForDatasetId({
          datasetId: newDataset.data.id,
          data: weightValues
        })
      );
    }

    return newDataset;
  };

  /** Function handler to add deleted datasets into the Redux Store `datasets` slice */
  const handleAddDeletedDataset = async (
    datasetMetadata: DatasetDetailResponse,
    datasetIndex: number
  ) => {
    const datasetIdWithIndex = `${datasetMetadata.id}_${datasetIndex}`;

    /** Dataset name that is displayed in the Dataset List e.g. `Population by province (1)` */
    const datasetNameWithIndex =
      datasetIndex > 1
        ? `${datasetMetadata.name} (${datasetIndex - 1})`
        : datasetMetadata.name;

    const newDeletedDataset = createNewDataset({
      dataset: datasetMetadata,
      datasetId: datasetIdWithIndex,
      datasetName: datasetMetadata.display_name
        ? datasetMetadata.display_name
        : datasetNameWithIndex,
      attributesSchema: []
    });

    dispatch(addDataset({ dataset: newDeletedDataset }));
    dispatch(
      incrementDatasetCount({
        baseId: datasetMetadata.id,
        usedIndex: datasetIndex
      })
    );
    dispatch(cleanupVisualizationOrder());

    return newDeletedDataset;
  };

  /** Function handler to add datasets by passing dataset and its weight values */
  const handleAddDatasetAndWeightValues = async (
    datasetMetadata: DatasetDetailResponse,
    datasetIndex: number,
    datasetData: DatasetData,
    savedFilter: DatasetFilter[],
    attributesSchema: DatasetAttributeSchema[],
    weightValues: {
      weight: string;
      attributes: Record<string, WeightValueEntry>;
    },
    styleConfig?: StyleConfig
  ) => {
    const featureCollection = convertDatasetResponseToFeatureCollection(
      datasetData,
      attributesSchema
    );
    const datasetId = `${datasetMetadata.id}_${datasetIndex}`;
    const datasetName =
      datasetIndex > 1
        ? `${datasetMetadata.name} (${datasetIndex - 1})`
        : datasetMetadata.name;
    const newDataset = createNewDataset({
      dataset: datasetMetadata,
      datasetId,
      datasetName: datasetMetadata.display_name
        ? datasetMetadata.display_name
        : datasetName,
      attributesSchema: attributesSchema,
      styleConfig,
      totalRows: datasetData.rows.length
    });

    handleAddDatasetAndFeatureData(
      datasetMetadata.id,
      newDataset,
      datasetIndex,
      savedFilter,
      featureCollection
    );

    // Check if exist weight values for the dataset will be added, otherwise initialize weight values
    if (Object.keys(weightValues).length > 0) {
      dispatch(
        setWeightValuesForDatasetId({
          datasetId: newDataset.data.id,
          data: weightValues
        })
      );
    } else dispatch(initializeWeightValues());
    dispatch(addRawDataset({ dataset: datasetData, id: datasetId }));
    if (isAdminTreeEnabled) {
      dispatch(setInputRowCount({ datasetId, count: datasetData.rows.length }));
    }
  };

  const handleClearDataset = (options?: {
    preserveWeightConfigBaseline?: boolean;
  }) => {
    const visualizationOrder = store.getState().dataset.visualizationOrder;

    visualizationOrder.forEach((datasetId) => {
      const dataset = datasets[datasetId];
      if (dataset?.data?.available)
        handleRemoveSourceLayer(
          dataset.data.id,
          dataset.properties.spatialType
        );
    });

    // Clear all datasets in the store, to avoid duplicate datasets
    dispatch(clearDataset(options));
    if (isAdminTreeEnabled) {
      dispatch(clearInputRowCounts());
    }
  };

  const handleAddSpatialAnalysisDatasetByType = ({
    spatialType,
    newDataset,
    featureCollection
  }: {
    spatialType: string;
    newDataset: Dataset;
    featureCollection: FeatureCollection;
  }) => {
    // Handle different spatial types
    if (
      spatialType === LayerType.POLYGON ||
      spatialType === LayerType.ADMINISTRATIVE
    ) {
      handleAddPolygonLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.POINT) {
      handleAddPointsLayer(newDataset, featureCollection);
    } else if (spatialType === EOutputAnalysisType.GRID) {
      handleAddGridLayer(newDataset, featureCollection);
    } else if (spatialType === EOutputAnalysisType.PROFILING) {
      // Site profiling can have both point and polygon geometries
      // Check the geometry types in the feature collection to determine how to handle them
      handleAddSiteProfiling(newDataset, featureCollection);
    } else if (
      spatialType === LayerType.H3 ||
      spatialType === LayerType.GEOHASH
    ) {
      handleAddH3GeohashLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.LINE) {
      handleAddLineLayer(newDataset, featureCollection);
    }
  };

  const handleAddSpatialAnalysisDataset = async (
    data: DatasetData,
    schema: DatasetAttributeSchema[] = [],
    isPreviewMode: boolean = false // When in preview mode, the dataset should be visible
  ) => {
    const attributes = createDatasetAttributes(data.columns);
    const defaultCategory = getDefaultSelectedCategory(
      attributes,
      data.spatial_aggregation_type
    );

    const filteredAttributes = attributes.filter(
      (attr) => attr.attribute_name !== ROW_ID_DETAIL_DATASET
    );
    const filteredSchema = schema?.filter(
      (attr) => attr.is_visible && attr.attribute_name !== ROW_ID_DETAIL_DATASET
    );
    const featureCollection = convertDatasetResponseToFeatureCollection(
      data,
      filteredSchema
    );

    const { isVisible: defaultVisible, visibility: defaultVisibility } =
      getDatasetVisibility(true, data.name);
    const isVisible = isPreviewMode || defaultVisible;
    const visibility = isPreviewMode ? "visible" : defaultVisibility;

    const newDataset: Dataset = {
      data: {
        schema,
        id: data.dataset_id,
        name: data.name,
        display_name: data.display_name ?? data.name,
        spatial_aggregation_type: data.spatial_aggregation_type,
        country_id: "id",
        source: "bvt",
        short_description: "",
        published: true,
        created_at: "2021-01-01",
        updated_at: "2021-01-01",
        dataset_size_kb: data.dataset_size_kb,
        available: true,
        total_rows: data.rows.length
      },
      properties: {
        isVisible,
        spatialType: data.spatial_aggregation_type
      },
      style: {
        weight: 1,
        opacity: 1,
        color: "#fff",
        fillColor: COLORS.TEAL[500],
        colorFamily: EColorNames.TEAL,
        fillOpacity: 0.7,
        visibility,
        colorRanges: [],
        stroke: {
          attribute_id: defaultCategory?.id ?? "",
          attribute_name: defaultCategory?.attribute_name ?? "",
          width: 1,
          opacity: 0.7
        },
        labelConfig: {
          color_scale: EColorLabel.BLACK_WHITE,
          attribute_id: defaultCategory?.id ?? "",
          attribute_name: defaultCategory?.attribute_name ?? "",
          show_label: false,
          opacity: 0.7
        },
        rankConfig: {
          isEnabled: false,
          maxRanking: 10
        },
        colorSteps: DEFAULT_STEP,
        // New POI layers default to the "Simple" Color Scale per PRD (matches
        // dataset creation). Seed the singleStyleConfig too so the layer is BUILT
        // with the flat fill (not the raw fillColor) and the map matches the
        // panel/legend immediately — without this the Simple style only
        // integrates after the user edits it.
        ...(data.spatial_aggregation_type === "poi"
          ? {
              colorScaleMethod: "simple" as const,
              // Seed both the live and saved slots so the panel opens clean
              // (not dirty) and Cancel has a baseline to revert to.
              singleStyleConfig: getDefaultSingleStyleConfig(
                defaultCategory?.attribute_name ?? ""
              ),
              singleStyleConfigSaved: getDefaultSingleStyleConfig(
                defaultCategory?.attribute_name ?? ""
              )
            }
          : {})
      },
      categories: filteredAttributes,
      selectedCategory: defaultCategory ?? {
        id: "",
        attribute_name: "",
        data_type: DataSchemaType.INT,
        is_nullable: true,
        is_spatial: false,
        position: 0,
        created_at: "",
        updated_at: "",
        is_reference_geo: false,
        is_reference_id: false,
        is_visible: true,
        paired_key_attribute_id: null
      }
    };
    dispatch(addDataset({ dataset: newDataset }));
    dispatch(addRawDataset({ dataset: data, id: data.dataset_id }));

    // Handle different spatial types
    handleAddSpatialAnalysisDatasetByType({
      spatialType: data.spatial_aggregation_type,
      newDataset,
      featureCollection
    });
  };

  const addSpatialAnalysisDataset = async (data: DatasetData) => {
    const featureCollection = convertDatasetResponseToFeatureCollection(data);
    const attributes = createDatasetAttributes(data.columns);

    const newDataset: Dataset = createDefaultSpatialDataset({
      data,
      attributes,
      isAnalysisResult: data.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME // Check if this is a spatial analysis result
    });
    dispatch(addDataset({ dataset: newDataset }));
    dispatch(addRawDataset({ dataset: data, id: data.dataset_id }));

    const spatialType = data.spatial_aggregation_type;

    if (spatialType === EOutputAnalysisType.GRID) {
      handleAddGridLayer(newDataset, featureCollection);
    } else if (spatialType === EOutputAnalysisType.PROFILING) {
      // Site profiling can have both point and polygon geometries
      // Check the geometry types in the feature collection to determine how to handle them
      handleAddSiteProfiling(newDataset, featureCollection);
    } else if (
      spatialType === LayerType.H3 ||
      spatialType === LayerType.GEOHASH
    ) {
      handleAddH3GeohashLayer(newDataset, featureCollection);
    } else if (spatialType === LayerType.LINE) {
      handleAddLineLayer(newDataset, featureCollection);
    }
  };

  const addNonSpatialAnalysisDataset = async (data: DatasetData) => {
    const attributes = createDatasetAttributes(data.columns);
    const newDataset = createDefaultSpatialDataset({
      data,
      attributes,
      isAnalysisResult: true
    });

    dispatch(addDataset({ dataset: newDataset }));
    dispatch(addRawDataset({ dataset: data, id: data.dataset_id }));
  };

  const addAnalysisDatasetGeometry = async (data: DatasetData) => {
    dispatch(
      setDatasetIsFetching({ datasetId: data.dataset_id, isFetching: false })
    );
    dispatch(addRawDataset({ dataset: data, id: data.dataset_id }));
  };

  const handleRemoveDataset = (
    datasetId: string,
    spatialType: SpatialAggregationType
  ) => {
    const isRemovingLastDataset =
      Boolean(datasets[datasetId]) && Object.keys(datasets).length === 1;

    dispatch(removeDataset(datasetId));
    dispatch(removeWidgetFromDataset(datasetId));
    if (isAdminTreeEnabled) {
      dispatch(removeInputRowCount(datasetId));
    }
    dispatch(removeRawDataset(datasetId));
    handleRemoveSourceLayer(datasetId, spatialType);
    // Reinitialize weight values across existing datasets after removing a dataset
    dispatch(initializeWeightValues());
    // Advanced normalization is only valid for the dataset attributes it was
    // configured against. Once the final dataset is gone, clear its persisted
    // signal so a newly added dataset starts in simple mode and does not submit
    // without the required log_mean/log_std_dev values.
    if (
      isRemovingLastDataset &&
      spatialConfig.scoringOption === ScoringOptionsEnum.LOG_Z_SCORE
    ) {
      dispatch(
        setSpatialConfig({
          ...spatialConfig,
          scoringOption: ScoringOptionsEnum.NORMALIZED
        })
      );
    }
    // Check and remove selected feature if it belongs to the removed dataset
    if (selectedFeature?.source === datasetId) {
      dispatch(setSelectedFeature(null));
    }
  };

  const handleToggleDatasetVisibility = (
    datasetId: string,
    showLabel: boolean = false,
    isRankEnabled: boolean = false,
    forceVisibility?: boolean
  ) => {
    if (!map) return;

    const dataset = datasets[datasetId];

    if (!dataset || !dataset.properties) return;

    const isNewLayer = !getSourceProperties(map, datasetId);

    if (isNewLayer) {
      const rawDataset = rawDatasets[datasetId];

      if (!dataset.data) return;

      const featureCollection =
        convertDatasetResponseToFeatureCollection(rawDataset);
      const spatialType = dataset.data.spatial_aggregation_type;

      if (
        spatialType === LayerType.POLYGON ||
        spatialType === LayerType.ADMINISTRATIVE
      ) {
        handleAddPolygonLayer(dataset, featureCollection);
      } else if (spatialType === LayerType.POINT) {
        handleAddPointsLayer(dataset, featureCollection);
      } else if (
        spatialType === LayerType.H3 ||
        spatialType === LayerType.GEOHASH
      ) {
        handleAddH3GeohashLayer(dataset, featureCollection);
      } else if (spatialType === LayerType.LINE) {
        handleAddLineLayer(dataset, featureCollection);
      }

      const layersByDatasetId: { [datasetId: string]: string[] } = {};

      for (let j = 0; j < visualizationOrder.length; j++) {
        const vizDatasetId = visualizationOrder[j];

        if (
          vizDatasetId !== datasetId &&
          !getSourceProperties(map, vizDatasetId)
        ) {
          continue;
        }

        const layers =
          SUBLAYERS(vizDatasetId)[
            datasets[vizDatasetId].properties.spatialType
          ];

        if (layers) {
          layersByDatasetId[vizDatasetId] = Object.values(layers);
        }
      }

      const datasetIds = Object.keys(layersByDatasetId);

      for (let i = datasetIds.length - 1; i >= 0; i--) {
        const datasetId = datasetIds[i];
        const layers = layersByDatasetId[datasetId];

        layers.forEach((layerId) => {
          map.moveLayer(layerId);
        });
      }
    }

    const { isVisible } = dataset.properties;
    const spatial_aggregation_type = dataset.data?.spatial_aggregation_type;

    if (isVisible === undefined) return;

    const newVisibility =
      forceVisibility ?? (isNewLayer ? isVisible : !isVisible);

    if (newVisibility !== isVisible || isNewLayer) {
      toggleLayerVisibilityById(
        map,
        datasetId,
        newVisibility,
        showLabel,
        isRankEnabled
      );

      if (spatial_aggregation_type === EOutputAnalysisType.PROFILING) {
        if (newVisibility) showAllMarkers();
        else hideAllMarkers();
      }

      if (newVisibility !== isVisible) {
        dispatch(
          toggleDatasetVisibility({ datasetId, visibility: newVisibility })
        );
      }
    }
  };

  const handleReorderDataset = (datasetId: string, newIndex: number) => {
    const currentIndex = visualizationOrder.indexOf(datasetId);

    if (currentIndex === -1) return;

    const newVisualizationOrder = [...visualizationOrder];

    newVisualizationOrder.splice(currentIndex, 1);
    newVisualizationOrder.splice(newIndex, 0, datasetId);

    dispatch(reorderDatasets(newVisualizationOrder));

    if (!map) return;
    const style = map.getStyle();

    if (!style?.layers) return;

    const layersByDatasetId: Record<string, string[]> = {};
    // The bottom-most area input layer (administrative / catchment boundary)
    // acts as a boundary: dataset layers are always moved *before* it so they
    // stay below the area input layers. Stays undefined when there is no area
    // input, which makes moveLayer append to the top as before. ORB-3289.
    let areaInputBeforeId: string | undefined;

    style.layers.forEach((layer) => {
      if (!("source" in layer) || !layer.source) return;

      if (
        !areaInputBeforeId &&
        AREA_INPUT_SOURCE_IDS.some((prefix) => layer.source.startsWith(prefix))
      ) {
        areaInputBeforeId = layer.id;
      }

      const owningDatasetId = newVisualizationOrder.find((id) =>
        layer.source.startsWith(id)
      );

      if (!owningDatasetId) return;

      if (!layersByDatasetId[owningDatasetId]) {
        layersByDatasetId[owningDatasetId] = [];
      }

      layersByDatasetId[owningDatasetId].push(layer.id);
    });

    for (let i = newVisualizationOrder.length - 1; i >= 0; i--) {
      const id = newVisualizationOrder[i];
      const layers = layersByDatasetId[id] ?? [];

      layers.forEach((layerId) => {
        map.moveLayer(layerId, areaInputBeforeId);
      });
    }

    // Keep the catchment radius + centroid overlay above the reordered dataset
    // layers (drawn on click; not owned by any dataset, so handled here).
    style.layers
      .filter(
        (layer) =>
          "source" in layer &&
          layer.source?.startsWith(CATCHMENT_RADIUS_RESULT_SOURCE_ID)
      )
      .forEach((layer) => map.moveLayer(layer.id));
  };

  const handleChangeSelectedCategoryDataset = (
    dataset: Dataset,
    selectedCategoryName: string,
    colorFamily: EColorNames,
    step: number = 10
  ) => {
    const selectedCategoryMetadata = dataset.categories.find(
      (category) => category.attribute_name === selectedCategoryName
    );

    // The new column regenerates the legend items, so hidden-legend filters
    // keyed by the previous attribute's values no longer apply — clear them
    // (matches the region-change reset in adminAreaSettings). Without this,
    // stale filters keep the old hidden items applied on the new column.
    dispatch(setLegendFilters({ ...legendFilters, [dataset.data.id]: [] }));

    // When the layer uses the Simple Color Scale, changing the column must keep
    // the flat fill/outline — it must NOT re-derive ordinal/quantile colors
    // from the new attribute's data type. (Flag-gated; legacy stays unchanged.)
    if (
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "simple"
    ) {
      // Keep the flat fill, but the Simple legend label defaults to the selected
      // attribute name — follow the new column unless the user customized it.
      const prevAttribute = dataset.selectedCategory?.attribute_name;
      const existing = dataset.style.singleStyleConfig;
      const label =
        !existing || existing.label === prevAttribute
          ? selectedCategoryName
          : existing.label;
      const singleStyleConfig = {
        ...(existing ?? getDefaultSingleStyleConfig(selectedCategoryName)),
        label
      };
      dispatch(
        commitDatasetSingleStyleConfig({
          datasetId: dataset.data.id,
          singleStyleConfig
        })
      );
      repaintSingleStyleLayer(dataset, singleStyleConfig);
      dispatch(
        updateDatasetSelectedCategory({
          datasetId: dataset.data.id,
          selectedCategoryName
        })
      );
      return;
    }

    // Custom Ordinal: changing the column repaints the per-category colors when
    // values are assigned, otherwise the default ordinal scale (NOT all "Other").
    if (
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_ordinal"
    ) {
      // Column change → regenerate the categories from the NEW attribute's
      // unique values (drops the previous attribute's assignments, per PRD). The
      // config panel re-mounts via its `key={attribute}` and hydrates this fresh
      // config. Seed from the active color scale step number: all unique values
      // when they fit, otherwise the first (step - 1) with the rest in "Other".
      const colorFamily = dataset.style.colorFamily;
      const datasetForNewAttribute = {
        ...dataset,
        selectedCategory: selectedCategoryMetadata ?? dataset.selectedCategory
      };
      const step = dataset.style.colorSteps || DEFAULT_STEP;
      const customCategoryConfig = getDefaultCustomCategoryConfig(
        colorFamily,
        step,
        getUniqueAttributeValues(datasetForNewAttribute)
      );
      // A column change regenerates a fresh auto-seed for the new attribute →
      // unconfirmed, so it follows the active color scale until saved.
      dispatch(
        seedDatasetCustomCategoryConfig({
          datasetId: dataset.data.id,
          customCategoryConfig
        })
      );
      repaintCustomCategoryOrDefault(
        {
          ...datasetForNewAttribute,
          style: { ...datasetForNewAttribute.style, customCategoryConfig }
        },
        customCategoryConfig
      );
      dispatch(
        updateDatasetSelectedCategory({
          datasetId: dataset.data.id,
          selectedCategoryName
        })
      );
      return;
    }

    // Custom Breaks: changing the column regenerates the break ranges from the
    // NEW attribute's numeric MIN/MAX (equal interval), keeping the current break
    // count — mirroring how Custom Ordinal/Simple follow the attribute change. If
    // the new attribute isn't numeric-resolvable, fall back to the value-based
    // paint.
    if (
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_breaks"
    ) {
      const newCategory = selectedCategoryMetadata ?? dataset.selectedCategory;
      const newAttribute = newCategory?.attribute_name ?? selectedCategoryName;
      const source = getSourceProperties(map, dataset.data.id);
      const features =
        source && !Array.isArray(source) && Array.isArray(source.features)
          ? source.features
          : [];
      const range = getNumericRange(
        features.length
          ? features.map((feature) => feature.properties?.[newAttribute])
          : getAttributeTableValues(rawDatasets[dataset.data.id], newAttribute)
      );
      const nextColorFamily = dataset.style.colorFamily;
      // Fresh count from the data (like re-adding the dataset), not the old
      // break count.
      const step = getCustomRangeDefaultCount(dataset);
      const datasetForNewAttribute = {
        ...dataset,
        selectedCategory: newCategory
      };
      if (range) {
        const customRangeConfig = getDefaultCustomRangeConfig(
          nextColorFamily,
          step,
          range.min,
          range.max,
          isIntegerDataType(newCategory?.data_type)
        );
        dispatch(
          commitDatasetCustomRangeConfig({
            datasetId: dataset.data.id,
            customRangeConfig
          })
        );
        repaintCustomRangeLayer(
          {
            ...datasetForNewAttribute,
            style: { ...datasetForNewAttribute.style, customRangeConfig }
          },
          customRangeConfig
        );
      } else {
        const layerType =
          SPATIAL_TYPE_TO_LAYER_TYPE[dataset.properties.spatialType];
        resetSingleStyleMarker(
          map,
          SUBLAYERS(dataset.data.id)[layerType],
          dataset.style.fillOpacity ?? 0.7
        );
        handleChangeColorDataLayers(
          datasetForNewAttribute,
          newCategory,
          nextColorFamily,
          step
        );
      }
      dispatch(
        updateDatasetSelectedCategory({
          datasetId: dataset.data.id,
          selectedCategoryName
        })
      );
      return;
    }

    if (
      dataset.data.spatial_aggregation_type === EOutputAnalysisType.PROFILING
    ) {
      PROFILING_LAYER_TYPES.forEach((type) => {
        handleChangeColorDataLayers(
          dataset,
          selectedCategoryMetadata as DatasetAttributeSchema,
          colorFamily,
          step,
          type
        );
      });
    } else {
      handleChangeColorDataLayers(
        dataset,
        selectedCategoryMetadata as DatasetAttributeSchema,
        colorFamily,
        step || 10
      );
    }

    dispatch(
      updateDatasetSelectedCategory({
        datasetId: dataset.data.id,
        selectedCategoryName
      })
    );
  };

  const handleChangeColorDataset = (
    datasetId: string,
    colorFamily: EColorNames,
    colorRanges: {
      min?: number | null;
      max?: number | null;
      value?: string | null;
      color: string;
      label: string;
    }[],
    selectedCategoryName: string,
    spatialType: SpatialAggregationType,
    dataType: DataSchemaType,
    steps?: number
  ) => {
    const dataset = datasets[datasetId];

    // Custom Ordinal (flag-gated): the palette doesn't drive a value-based
    // ordinal scale — instead recolor the category steps from the new palette
    // and repaint Custom Ordinal, so the steps follow the color scale on the
    // map + legend (the config panel recolors itself in parallel).
    if (
      isCustomMapVisualizationLegendEnabled &&
      dataset?.style.colorScaleMethod === "custom_ordinal"
    ) {
      dispatch(updateDatasetColorStyle({ datasetId, colorFamily }));

      // Recolor in place: keep the Custom Ordinal's OWN category count (its
      // config length) — a palette change must not resize it to the Ordinal
      // `colorSteps`. Only seed from `colorSteps` when there's no config yet.
      const existingConfig = dataset.style.customCategoryConfig;
      const stepCount =
        existingConfig?.steps.length ||
        dataset.style.colorSteps ||
        DEFAULT_STEP;
      const customCategoryConfig = existingConfig
        ? recolorCustomCategoryConfig(existingConfig, colorFamily, stepCount)
        : getDefaultCustomCategoryConfig(colorFamily, stepCount);
      dispatch(
        commitDatasetCustomCategoryConfig({ datasetId, customCategoryConfig })
      );
      // Per-category colors when values are assigned; otherwise the default
      // ordinal scale recolored to the new palette (not all "Other").
      repaintCustomCategoryOrDefault(
        {
          ...dataset,
          style: {
            ...dataset.style,
            colorFamily,
            customCategoryConfig,
            customCategoryConfigSaved: customCategoryConfig
          }
        },
        customCategoryConfig
      );
      return;
    }

    // Custom Breaks (flag-gated): a palette change recolors the break rows from
    // the new palette (keeping the break VALUES/labels), then repaints — so the
    // config, map and legend follow the color scale. The panel recolors itself in
    // parallel (its draft is discarded when `colorFamily` changes).
    if (
      isCustomMapVisualizationLegendEnabled &&
      dataset?.style.colorScaleMethod === "custom_breaks"
    ) {
      dispatch(updateDatasetColorStyle({ datasetId, colorFamily }));

      const existingConfig = dataset.style.customRangeConfig;
      let customRangeConfig = existingConfig
        ? recolorCustomRangeConfig(existingConfig, colorFamily)
        : undefined;
      // No saved config yet (palette changed before the first repaint seeded it):
      // seed a fresh default in the new palette when the numeric range is known.
      if (!customRangeConfig) {
        const range = getAttributeNumericRange(datasetId);
        if (!range) return;
        customRangeConfig = getDefaultCustomRangeConfig(
          colorFamily,
          getCustomRangeDefaultCount(dataset),
          range.min,
          range.max,
          isIntegerDataType(getStyledCategory(dataset)?.data_type)
        );
      }
      dispatch(
        commitDatasetCustomRangeConfig({ datasetId, customRangeConfig })
      );
      repaintCustomRangeLayer(
        {
          ...dataset,
          style: { ...dataset.style, colorFamily, customRangeConfig }
        },
        customRangeConfig
      );
      return;
    }

    handleChangeColorLayers(
      datasetId,
      colorFamily,
      colorRanges,
      selectedCategoryName,
      spatialType,
      dataType,
      steps
    );

    dispatch(updateDatasetColorStyle({ datasetId, colorFamily }));
  };

  // Unique values of the styled attribute, used to seed the Custom Ordinal
  // default. Prefers the map source (the values actually painted), falling back
  // to the tabular rows when it isn't readable yet.
  const getUniqueAttributeValues = (dataset: Dataset): string[] => {
    const attribute = getStyledCategory(dataset)?.attribute_name;
    if (!attribute) return [];
    const source = getSourceProperties(map, dataset.data.id);
    const features =
      source && !Array.isArray(source) && Array.isArray(source.features)
        ? source.features
        : [];
    if (features.length) {
      const unique = new Set<string>();
      features.forEach((feature) => {
        const value = feature.properties?.[attribute];
        if (value !== null && value !== undefined && value !== "") {
          unique.add(String(value));
        }
      });
      if (unique.size) return [...unique];
    }
    return getAttributeTableValues(rawDatasets[dataset.data.id], attribute);
  };

  // Numeric [min, max] of the layer's selected attribute, used to distribute
  // Custom Breaks by equal interval. Prefers the rendered map source features,
  // falls back to the loaded table rows, then to the numeric spread already
  // computed for the Quantile color ranges. Undefined when nothing numeric is
  // available yet (e.g. data not loaded) — the panel then disables Add/Remove.
  const getAttributeNumericRange = (
    datasetId: string
  ): { min: number; max: number } | undefined => {
    const dataset = datasets[datasetId];
    if (!dataset) return undefined;
    const attribute = getStyledCategory(dataset)?.attribute_name;
    if (!attribute) return undefined;

    const source = getSourceProperties(map, dataset.data.id);
    const features =
      source && !Array.isArray(source) && Array.isArray(source.features)
        ? source.features
        : [];
    if (features.length) {
      const range = getNumericRange(
        features.map((feature) => feature.properties?.[attribute])
      );
      if (range) return range;
    }

    const tableRange = getNumericRange(
      getAttributeTableValues(rawDatasets[datasetId], attribute)
    );
    if (tableRange) return tableRange;

    return getColorRangesMinMax(dataset.style.colorRanges ?? []) ?? undefined;
  };

  // Whether the layer's styled attribute holds null/empty values, painted as
  // "Other". Checked in order of reliability — the rendered features, then the
  // loaded rows, and `colorRanges` last because it keeps the previous
  // attribute's "null" bucket after a column change.
  const getAttributeHasEmptyValues = (datasetId: string): boolean => {
    const dataset = datasets[datasetId];
    if (!dataset) return false;
    const attribute = getStyledCategory(dataset)?.attribute_name;
    if (!attribute) return false;

    const onMap = hasEmptyAttributeValuesOnMap(map, dataset.data.id, attribute);
    if (onMap !== undefined) return onMap;

    const rawDataset = rawDatasets[datasetId];
    if (rawDataset?.rows?.length) {
      return hasEmptyAttributeTableValues(rawDataset, attribute);
    }

    return hasNullColorRange(dataset.style.colorRanges);
  };

  // The default/reset break count for Custom Breaks — derived from the CURRENT
  // data's row count exactly like a fresh dataset's Color Scale step
  // (`computeStepValue`: ≥10 rows → 10, else the row count, min 2). This makes
  // editing the attribute filter behave like re-adding the dataset: the count
  // follows the new data instead of a stale `colorRanges`. `rowCount` overrides
  // the store lookup when the fresh rows aren't committed yet (filter reload).
  const getCustomRangeDefaultCount = (
    dataset: Dataset,
    rowCount?: number
  ): number => {
    const rows = rowCount ?? rawDatasets[dataset.data.id]?.rows?.length ?? 0;
    return rows > 0
      ? computeStepValue(rows)
      : dataset.style.colorSteps || DEFAULT_STEP;
  };

  const handleChangeColorScaleMethod = (
    datasetId: string,
    method: ColorScaleMethod
  ) => {
    const stored = datasets[datasetId];
    if (!stored) return;

    // Restore config when switching unsaved config.
    dispatch(cancelDatasetSingleStyleConfig({ datasetId }));
    dispatch(cancelDatasetCustomCategoryConfig({ datasetId }));
    dispatch(cancelDatasetCustomRangeConfig({ datasetId }));
    const dataset: Dataset = {
      ...stored,
      style: {
        ...stored.style,
        singleStyleConfig:
          stored.style.singleStyleConfigSaved ?? stored.style.singleStyleConfig,
        customCategoryConfig:
          stored.style.customCategoryConfigSaved ??
          stored.style.customCategoryConfig,
        customRangeConfig:
          stored.style.customRangeConfigSaved ?? stored.style.customRangeConfig
      }
    };

    // Per-method color scale: restore THIS method's own palette (the one it had
    // last time it was active), falling back to the current active palette the
    // first time a method is used. Render with it so the map matches what the
    // reducer persists for `colorFamily` on switch.
    //
    // Exception: an UNCONFIRMED Custom Ordinal (auto-seed, not yet saved by the
    // user) has no palette of its own — it follows the CURRENT active palette
    // (e.g. Ordinal's red), so it must not restore a stale per-method value.
    const isUnconfirmedCustomCategory =
      method === "custom_ordinal" && !dataset.style.customCategoryConfigSaved;
    const colorFamily = isUnconfirmedCustomCategory
      ? dataset.style.colorFamily
      : (dataset.style.colorScaleSettings?.[method]?.colorFamily ??
        dataset.style.colorFamily);
    const datasetWithColorFamily: Dataset = {
      ...dataset,
      style: { ...dataset.style, colorFamily }
    };

    // Each Color Scale's config is preserved independently per layer (PRD:
    // switching scales must NOT destroy a previously confirmed config, and it's
    // restored exactly on switch-back). So we only SEED a default the first time
    // a scale is selected — never overwrite an existing `singleStyleConfig` /
    // `customCategoryConfig` — and repaint from the preserved config.
    // Attribute to style by: stroke attribute for lines, fill category otherwise.
    const styledCategory = getStyledCategory(dataset);

    if (method === "simple") {
      const singleStyleConfig =
        dataset.style.singleStyleConfig ??
        getDefaultSingleStyleConfig(styledCategory?.attribute_name ?? "");
      if (!dataset.style.singleStyleConfig) {
        dispatch(
          commitDatasetSingleStyleConfig({ datasetId, singleStyleConfig })
        );
      }
      repaintSingleStyleLayer(datasetWithColorFamily, singleStyleConfig);
    } else if (method === "custom_ordinal") {
      // Restore the last confirmed Custom Ordinal config when switching back;
      // on the very first switch, seed the PRD default — categories
      // auto-generated from the attribute's unique values. Repaint from the
      // config: per-category when it has assignments, the default ordinal scale
      // otherwise (`repaintCustomCategoryOrDefault` also clears any leftover
      // Simple image-marker state).
      const seeded =
        dataset.style.customCategoryConfig ??
        getDefaultCustomCategoryConfig(
          colorFamily,
          dataset.style.colorSteps || DEFAULT_STEP,
          getUniqueAttributeValues(dataset)
        );
      // Until the user SAVES, re-derive the category colors from the active
      // palette so a palette change made while on Ordinal is reflected here
      // (follow-Ordinal). A saved (confirmed) config keeps its own per-category
      // colors untouched.
      const customCategoryConfig = isUnconfirmedCustomCategory
        ? recolorCustomCategoryConfig(seeded, colorFamily, seeded.steps.length)
        : seeded;
      // Persist the (re)seeded config for the unconfirmed case — both the first
      // switch (no config yet) and a follow-Ordinal recolor of an existing seed.
      // Seed mode keeps the saved slot empty so it stays unconfirmed.
      if (isUnconfirmedCustomCategory) {
        dispatch(
          seedDatasetCustomCategoryConfig({ datasetId, customCategoryConfig })
        );
      }
      repaintCustomCategoryOrDefault(
        {
          ...datasetWithColorFamily,
          style: { ...datasetWithColorFamily.style, customCategoryConfig }
        },
        customCategoryConfig
      );
    } else if (method === "custom_breaks") {
      // Restore the last confirmed Custom Breaks config on switch-back; on the
      // very first switch, seed the PRD default — equal-interval ranges across
      // the dataset MIN/MAX, with the step count inherited from the previous
      // (Quantile) Color Scale setting. When no numeric range is available yet
      // (data not loaded), fall back to the value-based paint until it arrives.
      const range = getAttributeNumericRange(datasetId);
      if (range) {
        const customRangeConfig =
          dataset.style.customRangeConfig ??
          getDefaultCustomRangeConfig(
            colorFamily,
            getCustomRangeDefaultCount(dataset),
            range.min,
            range.max,
            isIntegerDataType(styledCategory?.data_type)
          );
        if (!dataset.style.customRangeConfig) {
          dispatch(
            commitDatasetCustomRangeConfig({ datasetId, customRangeConfig })
          );
        }
        repaintCustomRangeLayer(
          {
            ...datasetWithColorFamily,
            style: {
              ...datasetWithColorFamily.style,
              customRangeConfig
            }
          },
          customRangeConfig
        );
      } else {
        const layerType =
          SPATIAL_TYPE_TO_LAYER_TYPE[dataset.properties.spatialType];
        resetSingleStyleMarker(
          map,
          SUBLAYERS(dataset.data.id)[layerType],
          dataset.style.fillOpacity ?? 0.7
        );
        handleChangeColorDataLayers(
          datasetWithColorFamily,
          styledCategory,
          colorFamily,
          dataset.style.colorSteps || DEFAULT_STEP
        );
      }
    } else {
      // Value-based methods (ordinal/quantile) recompute the color
      // ranges from the live source data, which repaints the map and updates the
      // store the legend reads. These derive purely from the attribute +
      // colorFamily + colorSteps, so switching back restores them deterministically.
      const category = styledCategory;
      const step = dataset.style.colorSteps || DEFAULT_STEP;

      // Undo any Simple state first (hide the image marker, restore the dot) so
      // the value-based repaint isn't masked by leftover image markers.
      const layerType =
        SPATIAL_TYPE_TO_LAYER_TYPE[dataset.properties.spatialType];
      resetSingleStyleMarker(
        map,
        SUBLAYERS(dataset.data.id)[layerType],
        dataset.style.fillOpacity ?? 0.7
      );

      if (
        dataset.data.spatial_aggregation_type === EOutputAnalysisType.PROFILING
      ) {
        PROFILING_LAYER_TYPES.forEach((type) =>
          handleChangeColorDataLayers(
            datasetWithColorFamily,
            category,
            colorFamily,
            step,
            type
          )
        );
      } else {
        handleChangeColorDataLayers(
          datasetWithColorFamily,
          category,
          colorFamily,
          step
        );
      }
    }

    // Persist the method LAST: the value-based path runs `prepareLayerStyles`,
    // which dispatches a full-style replace built from the pre-change snapshot
    // (carrying the previous method). Dispatching here ensures the new method
    // wins so the Color Based On dropdown and the legend stay in sync. The
    // reducer also restores this method's palette into `colorFamily`.
    dispatch(updateDatasetColorScaleMethod({ datasetId, method }));

    // Repaint with the opacity this method was left at; the reducer already put it
    // back into the style.
    const restoredOpacity = dataset.style.colorScaleSettings?.[method]?.opacity;
    if (restoredOpacity !== undefined) {
      handleChangeOpacityLayers(
        datasetId,
        restoredOpacity,
        dataset.properties.spatialType
      );
    }
  };

  // Repaints a POI layer's points with the Simple flat fill/outline (falls back
  // to the legacy fill/outline colors when no Simple config has been set yet).
  const repaintSingleStyleLayer = (
    dataset: Dataset,
    singleStyleConfig?: SingleStyleValue
  ) => {
    const layerType =
      SPATIAL_TYPE_TO_LAYER_TYPE[dataset.properties.spatialType];
    const sublayers = SUBLAYERS(dataset.data.id)[layerType];
    changeColorSingleStyleLayer(
      map,
      sublayers,
      {
        fillColor: singleStyleConfig?.fillColor ?? dataset.style.fillColor,
        outlineColor: singleStyleConfig?.outlineColor ?? dataset.style.color,
        imageUrl: singleStyleConfig?.imageUrl ?? null,
        iconId: singleStyleConfig?.iconId ?? null
      },
      dataset.style.fillOpacity ?? 0.7
    );
  };

  // Live preview: every panel edit repaints the map/legend immediately while the
  // saved baseline stays put, so Cancel can revert to it.
  const handlePreviewSingleStyleConfig = (
    datasetId: string,
    singleStyleConfig: SingleStyleValue
  ) => {
    const dataset = datasets[datasetId];
    if (!dataset) return;
    dispatch(previewDatasetSingleStyleConfig({ datasetId, singleStyleConfig }));
    repaintSingleStyleLayer(dataset, singleStyleConfig);
  };

  // Save: commit the previewed config as the new saved baseline.
  const handleSaveSingleStyleConfig = (
    datasetId: string,
    singleStyleConfig: SingleStyleValue
  ) => {
    const dataset = datasets[datasetId];
    if (!dataset) return;
    dispatch(commitDatasetSingleStyleConfig({ datasetId, singleStyleConfig }));
    repaintSingleStyleLayer(dataset, singleStyleConfig);
  };

  // Cancel: revert the live config (and map/legend) to the last saved baseline.
  const handleCancelSingleStyleConfig = (datasetId: string) => {
    const dataset = datasets[datasetId];
    const saved = dataset?.style.singleStyleConfigSaved;
    if (!dataset || !saved) return;
    dispatch(cancelDatasetSingleStyleConfig({ datasetId }));
    repaintSingleStyleLayer(dataset, saved);
  };

  // Reset: commit the first-time default (fill/outline from the attribute) as the
  // new baseline and repaint. Returns the config so the panel mirrors it locally.
  const handleResetSingleStyleConfig = (
    datasetId: string
  ): SingleStyleValue | null => {
    const dataset = datasets[datasetId];
    if (!dataset) return null;
    const singleStyleConfig = getDefaultSingleStyleConfig(
      getStyledCategory(dataset)?.attribute_name ?? ""
    );
    dispatch(commitDatasetSingleStyleConfig({ datasetId, singleStyleConfig }));
    repaintSingleStyleLayer(dataset, singleStyleConfig);
    return singleStyleConfig;
  };

  // Repaints a layer's features with the Custom Ordinal per-value fill/outline
  // (data not assigned to any category falls back to the "Other" colors).
  const repaintCustomCategoryLayer = (
    dataset: Dataset,
    customCategoryConfig: CustomCategoryValue
  ) => {
    const layerType =
      SPATIAL_TYPE_TO_LAYER_TYPE[dataset.properties.spatialType];
    const sublayers = SUBLAYERS(dataset.data.id)[layerType];
    changeColorCustomCategoryLayer(
      map,
      sublayers,
      customCategoryConfig,
      getStyledCategory(dataset)?.attribute_name ?? "",
      dataset.style.fillOpacity ?? 0.7
    );
  };

  // Repaints a Custom Ordinal layer with the per-category colors when the user
  // has assigned values; otherwise falls back to the default (ordinal) color
  // scale — Custom Ordinal only "takes effect" once values are assigned + saved.
  const repaintCustomCategoryOrDefault = (
    dataset: Dataset,
    customCategoryConfig: CustomCategoryValue
  ) => {
    // Carry the incoming config on the dataset's style so any downstream
    // full-style replace (prepareLayerStyles spreads `dataset.style`) persists
    // THIS config — not the stale one from the pre-change snapshot. Without
    // this, resetting from a saved-custom layer would leave an assigned config
    // in the store while the map paints the default scale, desyncing the
    // legend (which gates on `hasCustomCategoryAssignments`).
    const datasetWithConfig: Dataset = {
      ...dataset,
      style: { ...dataset.style, customCategoryConfig }
    };
    if (hasCustomCategoryAssignments(customCategoryConfig)) {
      repaintCustomCategoryLayer(datasetWithConfig, customCategoryConfig);
      return;
    }
    const layerType =
      SPATIAL_TYPE_TO_LAYER_TYPE[datasetWithConfig.properties.spatialType];
    resetSingleStyleMarker(
      map,
      SUBLAYERS(datasetWithConfig.data.id)[layerType],
      datasetWithConfig.style.fillOpacity ?? 0.7
    );
    const step = datasetWithConfig.style.colorSteps || DEFAULT_STEP;
    // The default (value-based) scale colors by the styled attribute.
    const defaultCategory = getStyledCategory(datasetWithConfig);
    if (
      datasetWithConfig.data.spatial_aggregation_type ===
      EOutputAnalysisType.PROFILING
    ) {
      PROFILING_LAYER_TYPES.forEach((type) =>
        handleChangeColorDataLayers(
          datasetWithConfig,
          defaultCategory,
          datasetWithConfig.style.colorFamily,
          step,
          type
        )
      );
    } else {
      handleChangeColorDataLayers(
        datasetWithConfig,
        defaultCategory,
        datasetWithConfig.style.colorFamily,
        step
      );
    }
  };

  // Live preview: every panel edit repaints the map/legend immediately while the
  // saved baseline stays put, so Cancel can revert to it.
  const handlePreviewCustomCategoryConfig = (
    datasetId: string,
    customCategoryConfig: CustomCategoryValue
  ) => {
    const dataset = datasets[datasetId];
    if (!dataset) return;
    dispatch(
      previewDatasetCustomCategoryConfig({ datasetId, customCategoryConfig })
    );
    repaintCustomCategoryOrDefault(dataset, customCategoryConfig);
  };

  // Save: commit the previewed config as the saved baseline. Its presence marks
  // the config CONFIRMED — from now on it keeps its own colors and no longer
  // follows the active (Ordinal) scale.
  const handleSaveCustomCategoryConfig = (
    datasetId: string,
    customCategoryConfig: CustomCategoryValue
  ) => {
    const dataset = datasets[datasetId];
    if (!dataset) return;
    dispatch(
      commitDatasetCustomCategoryConfig({ datasetId, customCategoryConfig })
    );
    repaintCustomCategoryOrDefault(
      {
        ...dataset,
        style: {
          ...dataset.style,
          customCategoryConfig,
          customCategoryConfigSaved: customCategoryConfig
        }
      },
      customCategoryConfig
    );
  };

  // Cancel: revert to the last saved baseline; when nothing is saved yet
  // (unconfirmed), revert to a fresh auto-seed so the panel returns to default.
  const handleCancelCustomCategoryConfig = (datasetId: string) => {
    const dataset = datasets[datasetId];
    if (!dataset) return;
    const saved = dataset.style.customCategoryConfigSaved;
    const reverted =
      saved ??
      getDefaultCustomCategoryConfig(
        dataset.style.colorFamily,
        dataset.style.colorSteps || DEFAULT_STEP,
        getUniqueAttributeValues(dataset)
      );
    dispatch(
      saved
        ? cancelDatasetCustomCategoryConfig({ datasetId })
        : seedDatasetCustomCategoryConfig({
            datasetId,
            customCategoryConfig: reverted
          })
    );
    repaintCustomCategoryOrDefault(dataset, reverted);
  };

  // Changes the Custom Ordinal step count by updating ONLY its own config —
  // never the shared `colorSteps`, so the Ordinal step count stays independent.
  const handleChangeCustomCategoryStepCount = (
    datasetId: string,
    step: number
  ) => {
    const dataset = datasets[datasetId];
    if (!dataset) return;
    // Store the picked step so the panel shows it again after a save and reopen.
    dispatch(
      updateDatasetColorScaleSteps({
        datasetId,
        method: "custom_ordinal",
        steps: step
      })
    );
    const colorFamily = dataset.style.colorFamily;
    const isConfirmed = !!dataset.style.customCategoryConfigSaved;

    // Unconfirmed (auto-seed): re-seed from the attribute's unique values so
    // the category count tracks the step both ways; stays unconfirmed until saved.
    if (!isConfirmed) {
      const customCategoryConfig = getDefaultCustomCategoryConfig(
        colorFamily,
        step,
        getUniqueAttributeValues(dataset)
      );
      dispatch(
        seedDatasetCustomCategoryConfig({ datasetId, customCategoryConfig })
      );
      repaintCustomCategoryOrDefault(
        {
          ...dataset,
          style: {
            ...dataset.style,
            customCategoryConfig,
            customCategoryConfigSaved: undefined
          }
        },
        customCategoryConfig
      );
      return;
    }

    // Confirmed: resize the existing config in place, preserving assigned
    // values — to the reserve-aware active count (not the raw step), like the seed.
    const uniqueValues = getUniqueAttributeValues(dataset);
    const activeStepCount = getCustomCategoryActiveStepCount(
      uniqueValues.length,
      step
    );
    const baseConfig =
      dataset.style.customCategoryConfig ??
      getDefaultCustomCategoryConfig(colorFamily, step, uniqueValues);
    const customCategoryConfig = recolorCustomCategoryConfig(
      baseConfig,
      colorFamily,
      activeStepCount
    );
    dispatch(
      syncDatasetCustomCategoryConfig({ datasetId, customCategoryConfig })
    );

    // Carry the config in the saved slot too (like the sync reducer) so the
    // default-scale repaint's full style replace can't revert it.
    repaintCustomCategoryOrDefault(
      {
        ...dataset,
        style: {
          ...dataset.style,
          customCategoryConfig,
          customCategoryConfigSaved: customCategoryConfig
        }
      },
      customCategoryConfig
    );
  };

  // Sync reset the legend filter with the map layer: clearing the legend filter restores
  const resetLegendFilterForLayer = (dataset: Dataset) => {
    dispatch(setLegendFilters({ ...legendFilters, [dataset.data.id]: [] }));
    const rawDataset = rawDatasets[dataset.data.id];
    if (!rawDataset) return;
    const orders = visualizationOrder
      .filter((id) => datasets[id])
      .map((id) => ({
        id,
        type: datasets[id].data.spatial_aggregation_type
      }));
    handleFilterLayerByLegend(
      [],
      dataset,
      rawDataset,
      getBeforeId(dataset.data.id, orders)
    );
  };

  // Resets Custom Ordinal to its first-time default (re-seeded from the
  // attribute's unique values) and returns the config so the panel can mirror it.
  const handleResetCustomCategoryConfig = (
    datasetId: string
  ): CustomCategoryValue | null => {
    const dataset = datasets[datasetId];
    if (!dataset) return null;
    const attribute = getStyledCategory(dataset)?.attribute_name;
    const rawUniqueValues = attribute
      ? getAttributeTableValues(rawDatasets[datasetId], attribute)
      : [];
    const uniqueValues = rawUniqueValues.length
      ? rawUniqueValues
      : getUniqueAttributeValues(dataset);

    const rows = rawDatasets[datasetId]?.rows?.length ?? 0;
    const stepCount =
      rows > 0
        ? computeStepValue(rows)
        : dataset.style.colorSteps || DEFAULT_STEP;
    const customCategoryConfig = getDefaultCustomCategoryConfig(
      dataset.style.colorFamily,
      stepCount,
      uniqueValues
    );
    // Back to the auto-seed default → unconfirmed, so it resumes following
    // the active color scale until the user saves again.
    dispatch(
      seedDatasetCustomCategoryConfig({ datasetId, customCategoryConfig })
    );
    // Reset the stored step as well, so the Step input follows.
    dispatch(
      updateDatasetColorScaleSteps({
        datasetId,
        method: "custom_ordinal",
        steps: stepCount
      })
    );

    const datasetWithConfig: Dataset = {
      ...dataset,
      style: {
        ...dataset.style,
        customCategoryConfig,
        customCategoryConfigSaved: undefined
      }
    };

    resetLegendFilterForLayer(datasetWithConfig);
    repaintCustomCategoryOrDefault(datasetWithConfig, customCategoryConfig);
    return customCategoryConfig;
  };

  const repaintCustomRangeLayer = (
    dataset: Dataset,
    customRangeConfig: CustomRangeValue
  ) => {
    const layerType =
      SPATIAL_TYPE_TO_LAYER_TYPE[dataset.properties.spatialType];
    const sublayers = SUBLAYERS(dataset.data.id)[layerType];
    changeColorCustomRangeLayer(
      map,
      sublayers,
      customRangeConfig,
      getStyledCategory(dataset)?.attribute_name ?? "",
      dataset.style.fillOpacity ?? 0.7
    );
  };

  // Live preview: every panel edit repaints the map/legend immediately while the
  // saved baseline stays put, so Cancel can revert to it.
  const handlePreviewCustomRangeConfig = (
    datasetId: string,
    customRangeConfig: CustomRangeValue
  ) => {
    const dataset = datasets[datasetId];
    if (!dataset) return;
    dispatch(previewDatasetCustomRangeConfig({ datasetId, customRangeConfig }));
    repaintCustomRangeLayer(
      { ...dataset, style: { ...dataset.style, customRangeConfig } },
      customRangeConfig
    );
  };

  // Save: commit the previewed config as the new saved baseline.
  const handleSaveCustomRangeConfig = (
    datasetId: string,
    customRangeConfig: CustomRangeValue
  ) => {
    const dataset = datasets[datasetId];
    if (!dataset) return;
    dispatch(commitDatasetCustomRangeConfig({ datasetId, customRangeConfig }));
    repaintCustomRangeLayer(
      { ...dataset, style: { ...dataset.style, customRangeConfig } },
      customRangeConfig
    );
  };

  // Cancel: revert the live config (and map/legend) to the last saved baseline.
  const handleCancelCustomRangeConfig = (datasetId: string) => {
    const dataset = datasets[datasetId];
    const saved = dataset?.style.customRangeConfigSaved;
    if (!dataset || !saved) return;
    dispatch(cancelDatasetCustomRangeConfig({ datasetId }));
    repaintCustomRangeLayer(
      { ...dataset, style: { ...dataset.style, customRangeConfig: saved } },
      saved
    );
  };

  // Custom Breaks owns its step count independently of Quantile's `colorSteps`.
  // Changing it redistributes the breaks from the dataset MIN/MAX by equal
  // interval (PRD: values recalculated from scratch) — no-ops without a range.
  const handleChangeCustomRangeStepCount = (
    datasetId: string,
    step: number
  ) => {
    const dataset = datasets[datasetId];
    if (!dataset) return;
    const range = getAttributeNumericRange(datasetId);
    if (!range) return;
    // Store the picked step so the panel shows it again after a save and reopen.
    dispatch(
      updateDatasetColorScaleSteps({
        datasetId,
        method: "custom_breaks",
        steps: step
      })
    );
    const colorFamily = dataset.style.colorFamily;
    const isInteger = isIntegerDataType(getStyledCategory(dataset)?.data_type);
    const customRangeConfig = {
      ...(dataset.style.customRangeConfig ??
        getDefaultCustomRangeConfig(
          colorFamily,
          step,
          range.min,
          range.max,
          isInteger
        )),
      steps: redistributeCustomRange(
        step,
        range.min,
        range.max,
        colorFamily,
        isInteger
      )
    };
    dispatch(commitDatasetCustomRangeConfig({ datasetId, customRangeConfig }));
    repaintCustomRangeLayer(
      { ...dataset, style: { ...dataset.style, customRangeConfig } },
      customRangeConfig
    );
  };

  // Reset Custom Breaks back to its FIRST-TIME default: equal-interval ranges
  // across the current dataset MIN/MAX with the break count reset to the Color
  // Scale (Quantile) count derived from the data (`getCustomRangeDefaultCount`)
  // — NOT the current (possibly added/removed) break count. So Reset restores the
  // original data-driven number of breaks. Returns the config so the panel can
  // mirror it locally.
  const handleResetCustomRangeConfig = (
    datasetId: string
  ): CustomRangeValue | null => {
    const dataset = datasets[datasetId];
    if (!dataset) return null;
    const range = getAttributeNumericRange(datasetId);
    if (!range) return null;
    const step = getCustomRangeDefaultCount(dataset);
    const customRangeConfig = getDefaultCustomRangeConfig(
      dataset.style.colorFamily,
      step,
      range.min,
      range.max,
      isIntegerDataType(dataset.selectedCategory?.data_type)
    );
    dispatch(commitDatasetCustomRangeConfig({ datasetId, customRangeConfig }));
    // Reset the stored step as well, so the Step input follows.
    dispatch(
      updateDatasetColorScaleSteps({
        datasetId,
        method: "custom_breaks",
        steps: step
      })
    );

    const datasetWithConfig: Dataset = {
      ...dataset,
      style: {
        ...dataset.style,
        customRangeConfig,
        customRangeConfigSaved: customRangeConfig
      }
    };

    resetLegendFilterForLayer(datasetWithConfig);
    repaintCustomRangeLayer(datasetWithConfig, customRangeConfig);
    return customRangeConfig;
  };

  const handleChangeStrokeWidthDataset = (datasetId: string, width: number) => {
    const dataset = datasets[datasetId];

    if (!dataset) return;

    const layerType =
      SPATIAL_TYPE_TO_LAYER_TYPE[dataset.properties.spatialType];
    const sublayers = SUBLAYERS(datasetId)[layerType];

    changeStrokeWidthLayer(map, sublayers, width);

    dispatch(updateDatasetStrokeWidth({ datasetId, width }));
  };

  const handleChangeStrokeAttributeDataset = (
    dataset: Dataset,
    attributeName: string,
    colorFamily: EColorNames,
    step: number = 10
  ) => {
    const attribute = dataset.categories.find(
      (cat) => cat.attribute_name === attributeName
    );
    if (!attribute) return;

    // Point the seed helpers below at the NEW stroke column — the store dispatch
    // only flushes afterwards, so `dataset` still holds the previous attribute.
    const datasetForNewAttribute: Dataset = {
      ...dataset,
      style: {
        ...dataset.style,
        stroke: {
          ...dataset.style.stroke,
          attribute_id: attribute.id,
          attribute_name: attribute.attribute_name
        }
      }
    };

    const method = dataset.style.colorScaleMethod;
    const isCustomLegendMethod =
      method === "simple" ||
      method === "custom_ordinal" ||
      method === "custom_breaks";

    // Lines: changing the stroke column regenerates the active custom-legend
    // config from the new attribute (drops old assignments), like the fill path.
    if (
      isCustomMapVisualizationLegendEnabled &&
      dataset.properties.spatialType === LayerType.LINE &&
      isCustomLegendMethod
    ) {
      resetLegendFilterForLayer(datasetForNewAttribute);

      if (method === "simple") {
        const existing = dataset.style.singleStyleConfig;
        const prevAttribute = dataset.style.stroke?.attribute_name;
        const label =
          !existing || existing.label === prevAttribute
            ? attributeName
            : existing.label;
        const singleStyleConfig = {
          ...(existing ?? getDefaultSingleStyleConfig(attributeName)),
          label
        };
        dispatch(
          commitDatasetSingleStyleConfig({
            datasetId: dataset.data.id,
            singleStyleConfig
          })
        );
        repaintSingleStyleLayer(datasetForNewAttribute, singleStyleConfig);
      } else if (method === "custom_ordinal") {
        const seedStep = dataset.style.colorSteps || DEFAULT_STEP;
        const customCategoryConfig = getDefaultCustomCategoryConfig(
          dataset.style.colorFamily,
          seedStep,
          getUniqueAttributeValues(datasetForNewAttribute)
        );
        dispatch(
          seedDatasetCustomCategoryConfig({
            datasetId: dataset.data.id,
            customCategoryConfig
          })
        );
        repaintCustomCategoryOrDefault(
          {
            ...datasetForNewAttribute,
            style: { ...datasetForNewAttribute.style, customCategoryConfig }
          },
          customCategoryConfig
        );
      } else {
        // custom_breaks: rebuild equal-interval ranges from the new attribute's
        // MIN/MAX, or fall back to the value-based stroke paint when non-numeric.
        const source = getSourceProperties(map, dataset.data.id);
        const features =
          source && !Array.isArray(source) && Array.isArray(source.features)
            ? source.features
            : [];
        const range = getNumericRange(
          features.length
            ? features.map((feature) => feature.properties?.[attributeName])
            : getAttributeTableValues(
                rawDatasets[dataset.data.id],
                attributeName
              )
        );
        if (range) {
          const customRangeConfig = getDefaultCustomRangeConfig(
            dataset.style.colorFamily,
            getCustomRangeDefaultCount(dataset),
            range.min,
            range.max,
            isIntegerDataType(attribute.data_type)
          );
          dispatch(
            commitDatasetCustomRangeConfig({
              datasetId: dataset.data.id,
              customRangeConfig
            })
          );
          repaintCustomRangeLayer(
            {
              ...datasetForNewAttribute,
              style: { ...datasetForNewAttribute.style, customRangeConfig }
            },
            customRangeConfig
          );
        } else {
          handleChangeColorDataLayers(
            datasetForNewAttribute,
            attribute,
            colorFamily,
            step
          );
        }
      }

      dispatch(
        updateDatasetStrokeAttribute({
          datasetId: dataset.data.id,
          attributeId: attribute.id,
          attributeName: attribute.attribute_name
        })
      );
      return;
    }

    // Update layers similar to handleChangeSelectedCategoryDataset
    if (
      dataset.data.spatial_aggregation_type === EOutputAnalysisType.PROFILING
    ) {
      PROFILING_LAYER_TYPES.forEach((type) => {
        handleChangeColorDataLayers(
          dataset,
          attribute,
          colorFamily,
          step,
          type
        );
      });
    } else {
      handleChangeColorDataLayers(dataset, attribute, colorFamily, step);
    }

    dispatch(
      updateDatasetStrokeAttribute({
        datasetId: dataset.data.id,
        attributeId: attribute.id,
        attributeName: attribute.attribute_name
      })
    );
  };

  const handleChangeOpacityDataset = (datasetId: string, opacity: number) => {
    const dataset = datasets[datasetId];

    if (!dataset) return;

    handleChangeOpacityLayers(
      datasetId,
      opacity,
      dataset.properties.spatialType
    );

    dispatch(updateDatasetOpacity({ datasetId, opacity }));
  };

  const handleChangeStrokeOpacityDataset = (
    datasetId: string,
    opacity: number
  ) => {
    const dataset = datasets[datasetId];

    if (!dataset) return;

    handleChangeOpacityLayers(
      datasetId,
      opacity,
      dataset.properties.spatialType
    );

    dispatch(updateDatasetStrokeOpacity({ datasetId, opacity }));
  };

  const handleResetDatasetToHistoryDataset = () => {
    removeAllCatchmentMarkers();
    dispatch(resetDatasetToHistoryDataset());
    dispatch(resetWidgetToHistory());
    handleResetLayerOrder(history.visualizationOrder);
    handleResetLayerData(history.datasets, history.rawDatasets);
  };

  const handleUpdateDatasetData = async (
    datasetId: string,
    data: DatasetData,
    h3Configuration: H3Configuration | null = null,
    groupBy?: string
  ) => {
    const featureCollection = convertDatasetResponseToFeatureCollection(data);
    if (!datasets?.[datasetId]) return;
    //  Remove source layer from the map
    const dataset = datasets[datasetId];
    // Remove old source layer
    handleRemoveSourceLayer(dataset.data.id, dataset.properties.spatialType);
    // Add new source layer
    const spatialType = data.spatial_aggregation_type;
    if (
      spatialType === LayerType.POLYGON ||
      spatialType === LayerType.ADMINISTRATIVE
    ) {
      handleAddPolygonLayer(dataset, featureCollection);
    } else if (spatialType === LayerType.POINT) {
      handleAddPointsLayer(dataset, featureCollection);
    } else if (
      spatialType === LayerType.H3 ||
      spatialType === LayerType.GEOHASH
    ) {
      handleAddH3GeohashLayer(dataset, featureCollection);
    } else if (spatialType === LayerType.LINE) {
      handleAddLineLayer(dataset, featureCollection);
    } else if (spatialType === EOutputAnalysisType.PROFILING) {
      handleAddSiteProfiling(dataset, featureCollection);
    }
    if (h3Configuration)
      dispatch(updateDatasetH3Config({ datasetId, config: h3Configuration }));
    dispatch(
      updateWidgetH3Config({ widgetId: datasetId, config: h3Configuration })
    );

    if (groupBy) {
      dispatch(updateDatasetGroupBy({ datasetId, groupBy }));
      dispatch(updateWidgetGroupBy({ widgetId: datasetId, groupBy }));
    }

    dispatch(addRawDataset({ dataset: data, id: dataset.data.id }));
    dispatch(setLegendFilters({ ...legendFilters, [dataset.data.id]: [] }));

    // Reconcile Custom Ordinal to the freshly filtered data: keep each category's
    // already-selected values that still exist, drop the ones that disappeared,
    // and resize the step count to the new data cardinality (PRD: the category
    // persists, only vanished values are cleared). Runs off the just-built feature
    // collection (the authoritative new data). Only when Custom Ordinal is active
    // and there's data — never wipe assignments on an empty refresh.
    const existingConfig = dataset.style.customCategoryConfig;
    const attributeName = getStyledCategory(dataset)?.attribute_name;
    if (
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_ordinal" &&
      existingConfig &&
      attributeName
    ) {
      const features = Array.isArray(featureCollection.features)
        ? featureCollection.features
        : [];
      const unique = new Set<string>();
      features.forEach((feature) => {
        const value = feature.properties?.[attributeName];
        if (value !== null && value !== undefined && value !== "") {
          unique.add(String(value));
        }
      });
      if (unique.size) {
        const customCategoryConfig = reconcileCustomCategoryConfig(
          existingConfig,
          [...unique],
          dataset.style.colorFamily,
          dataset.style.colorSteps
        );
        dispatch(
          syncDatasetCustomCategoryConfig({ datasetId, customCategoryConfig })
        );

        repaintCustomCategoryOrDefault(
          {
            ...dataset,
            style: {
              ...dataset.style,
              customCategoryConfig,
              customCategoryConfigSaved: dataset.style.customCategoryConfigSaved
                ? customCategoryConfig
                : undefined
            }
          },
          customCategoryConfig
        );
      }
    }

    // Reconcile Custom Breaks to the freshly filtered data. Editing the attribute
    // filter produces new data, so the config is regenerated FROM SCRATCH — the
    // same as Reset: equal-interval breaks across the new MIN/MAX with the break
    // count re-derived from the Color Scale. Seeds a config if there isn't one
    // yet. The MIN/MAX is read straight from the just-arrived raw `data` (the
    // authoritative new rows) so it never falls back to a stale range; the feature
    // collection and live map source are only used if that lookup can't resolve.
    if (
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_breaks" &&
      attributeName
    ) {
      const features = Array.isArray(featureCollection.features)
        ? featureCollection.features
        : [];
      const range =
        getNumericRange(getAttributeTableValues(data, attributeName)) ??
        getNumericRange(
          features.map((feature) => feature.properties?.[attributeName])
        ) ??
        getAttributeNumericRange(datasetId);
      if (range) {
        const customRangeConfig = getDefaultCustomRangeConfig(
          dataset.style.colorFamily,
          // Count follows the NEW data's row count (fresh rows aren't in the
          // store yet), like re-adding the dataset.
          getCustomRangeDefaultCount(dataset, data.rows?.length),
          range.min,
          range.max,
          isIntegerDataType(getStyledCategory(dataset)?.data_type)
        );
        dispatch(
          commitDatasetCustomRangeConfig({ datasetId, customRangeConfig })
        );
        repaintCustomRangeLayer(
          { ...dataset, style: { ...dataset.style, customRangeConfig } },
          customRangeConfig
        );
      }
    }
  };

  const handleDisplayNameVisibility = (
    dataset: Dataset,
    visibleLabel: boolean
  ) => {
    dispatch(
      updateDatasetStyleVisibleLabel({
        datasetId: dataset.data.id,
        visible: visibleLabel
      })
    );
    const layerLabelId = `${dataset.data.id}-label`;
    handleChangeVisibleLabelLayer(layerLabelId, visibleLabel);
  };

  const handleDisplayName = (
    dataset: Dataset,
    selectedCategory?: DatasetAttributeSchema
  ) => {
    handleDisplayNameLabelLayerCategory(
      `${dataset.data.id}-label`,
      selectedCategory as DatasetAttributeSchema
    );
    dispatch(
      updateDatasetStyleAttributeLabel({
        datasetId: dataset.data.id,
        attribute_id: selectedCategory?.id || "",
        attribute_name: selectedCategory?.attribute_name || ""
      })
    );
  };

  const handleDisplayNameColor = (
    dataset: Dataset,
    selectedColor: EColorLabel
  ) => {
    handleChangeLabelLayerColor(`${dataset.data.id}-label`, selectedColor);
    handleChangeLabelLayerColor(`${dataset.data.id}-rank`, selectedColor);
    dispatch(
      updateDatasetStyleColorLabel({
        datasetId: dataset.data.id,
        color: selectedColor
      })
    );
  };

  const handleChangeLabelOpacity = (dataset: Dataset, opacity: number) => {
    handleChangeLabelLayerOpacity(`${dataset.data.id}-label`, opacity);
    handleChangeLabelLayerOpacity(`${dataset.data.id}-rank`, opacity);
    dispatch(
      updateDatasetStyleOpacityLabel({
        datasetId: dataset.data.id,
        opacity: opacity
      })
    );
  };

  const handleRankConfigChange = async (
    datasetId: string,
    style: DatasetStyle,
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    }
  ) => {
    if (!map) return;

    // check for custom id polygon source (site profiling analysis result)
    const sourceId = map.getSource(`${datasetId}-polygon`)
      ? `${datasetId}-polygon`
      : datasetId;

    // update source maplibre
    handleUpdateSourceWithRank({
      datasetId: sourceId,
      style,
      update
    });

    // update redux store
    if (update.isEnabled !== undefined) {
      const payload = { datasetId, isEnabled: update.isEnabled };
      handleStyleRankEnabledChange({
        ...payload,
        isShowLabel: style.labelConfig.show_label
      });
      dispatch(updateDatasetStyleEnabledRank(payload));
    }

    if (update.maxRanking !== undefined) {
      const payload = { datasetId, maxRanking: update.maxRanking };
      dispatch(updateDatasetStyleMaxRanking(payload));
    }
  };

  const handleHighlightDatasetFeatureByWkt = ({
    datasetId = null,
    value = null,
    type = "wkt"
  }: {
    datasetId: string | null;
    value: string | GeoJsonProperties | null;
    type?: "grid" | "wkt";
  }) => {
    if (!map || !datasetId || !value) return;

    let geometry: Geometry | null = null;

    if (type === "wkt") {
      const geometryFeature = wktToGeoJSON(value as string);
      if (!geometryFeature?.geometry) return;
      geometry = geometryFeature.geometry;
    } else if (type === "grid") {
      const feature = value as GeoJsonProperties;
      if (!feature?.geometry) return;
      geometry = feature.geometry as Geometry;
    }

    if (!geometry) return;

    /** Catchment polygon datasets are rendered under a prefixed source
     * (`catchment-dataset-polygon-${id}`) by useCatchmentArea, and they don't
     * live in the regular dataset registry — so the visibility/reorder steps
     * below are no-ops for them and the highlight feature-state must target
     * the prefixed source instead of the bare datasetId. Detect by checking
     * which source actually exists on the map.
     */
    const catchmentPolygonSourceId = `${CATCHMENT_DATASET_POLYGON_SOURCE_ID}-${datasetId}`;
    const isCatchmentPolygon = !!map.getSource(catchmentPolygonSourceId);

    if (!isCatchmentPolygon) {
      handleToggleDatasetVisibility(datasetId, false, false, true);
      handleReorderDataset(datasetId, 0);
    }

    return handleHighlightFeatureByGeometry(
      isCatchmentPolygon ? catchmentPolygonSourceId : datasetId,
      geometry
    );
  };

  return {
    handleClearDataset,
    handleRemoveDataset,
    handleAddDataset,
    handleAddOnboardingDataset,
    handleAddDatasetShowCatchment,
    handleAddDeletedDataset,
    handleAddSpatialAnalysisDataset,
    handleAddSpatialAnalysisDatasetByType,
    handleChangeStrokeWidthDataset,
    handleChangeStrokeAttributeDataset,
    handleChangeSelectedCategoryDataset,
    handleChangeColorScaleMethod,
    handleChangeCustomCategoryStepCount,
    handleResetCustomCategoryConfig,
    handlePreviewSingleStyleConfig,
    handleSaveSingleStyleConfig,
    handleCancelSingleStyleConfig,
    handleResetSingleStyleConfig,
    handlePreviewCustomCategoryConfig,
    handleSaveCustomCategoryConfig,
    handleCancelCustomCategoryConfig,
    handlePreviewCustomRangeConfig,
    handleSaveCustomRangeConfig,
    handleCancelCustomRangeConfig,
    handleChangeCustomRangeStepCount,
    handleResetCustomRangeConfig,
    getAttributeNumericRange,
    getAttributeHasEmptyValues,
    handleToggleDatasetVisibility,
    handleReorderDataset,
    handleChangeColorDataset,
    handleChangeOpacityDataset,
    handleChangeStrokeOpacityDataset,
    handleResetDatasetToHistoryDataset,
    handleAddDatasetAndWeightValues,
    handleUpdateDatasetData,
    addSpatialAnalysisDataset,
    addNonSpatialAnalysisDataset,
    addAnalysisDatasetGeometry,
    generateDatasetData,
    handleDisplayNameVisibility,
    handleDisplayName,
    handleDisplayNameColor,
    handleChangeLabelOpacity,
    handleCreateDatasetHistory,
    handleRankConfigChange,
    handleHighlightDatasetFeatureByWkt
  };
};
